import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { IoFunnel } from "react-icons/io5";
import Formsy from "formsy-react";
import queryString from "query-string";

import * as callActions from "../../actions/callActions";
import * as modalActions from "../../actions/modalActions";

import * as userActions from "../../actions/userActions";
import * as serviceActions from "../../actions/serviceActions";
import * as codeActions from "../../actions/codeActions";
import * as loaderActions from "../../actions/loaderActions";

import HeaderWrapper from "../common/HeaderWrapper";
import CallApprovalContainer from "./CallApprovalContainer";
import ConfirmModal from "../common/ConfirmModal";
import { customToast as toast } from "../../utils";
import CallService from "../../services/callService";
import ReportService from "../../services/reportService";
import { downloadCSV } from "../reports/utils";
import { INVOICE_DETAIL_COLUMNS } from "../reports/InvoiceDetail/columns";
import { formatDate } from "../../helpers/date";

import Loading from "../common/Loading";

import CallApprovalFilters from "./CallApprovalFilters";
import Modal from "../common/Modal";
import CallApprovalNotificationForm from "./CallApprovalNotificationForm";
import BcInvoiceExportSummaryModal from "./BcInvoiceExportSummaryModal";

import "./call.scss";

const INITIAL_FILTER_STATE = {
  customer: [],
  project: [],
  multi_assigned_to: [],
  department: [],
  start_date: "",
  end_date: "",
  call_type: [],
  grouped_by_customer: "",
  invoice_frequency: [],
  project_manager: []
};

const CallApprovalPage = ({
  auth,
  actions,
  codes,
  services,
  loading,
  users,
  pathname,
  callApprovalToNotify
}) => {
  const [state, setState] = useState({
    call: false,
    open: true,
    focusedInput: null
  });
  const [localPathname, setLocalPathname] = useState(null);
  const [reviewType, setReviewType] = useState(null);
  const [groupedCalls, setGroupedCalls] = useState({});
  const [disableInvoiceExport, setDisableInvoiceExport] = useState(true);
  const [invoiceSummaryResults, setInvoiceSummaryResults] = useState({});
  const [loadingExport, setLoadingExport] = useState(false);

  const [filters, setFilters] = useState(
    localStorage.getItem("CALL_APPROVAL_FILTERS")
      ? JSON.parse(localStorage.getItem("CALL_APPROVAL_FILTERS"))
      : INITIAL_FILTER_STATE
  );
  const [needFetchData, setNeedFetchData] = useState(false);
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    let dis = true;
    if (filters.call_type && filters.call_type.length === 1) {
      const callType = filters.call_type[0];
      if (callType && callType.label === "BILLABLE") {
        dis = false;
      }
    }
    setDisableInvoiceExport(dis);
  }, [filters]);

  const loadCalls = useCallback(
    async (showLoader) =>
      new Promise(async (resolve, reject) => {
        try {
          actions.toggleLoader(showLoader);
          setNeedFetchData(false);
          let stageValue;
          if (reviewType === "Finance") {
            // Submitted
            stageValue = "71S";
          } else if (reviewType === "Manager") {
            // Completed
            stageValue = "70C";
          } else if (reviewType === "Invoice") {
            // Ready to Invoice
            stageValue = "80R";
          } else if (reviewType === "Billing") {
            // Invoiced (Exported)
            stageValue = "89E";
          }
          const stage = codes.find(
            (c) => c.entity === "Call" && c.value === stageValue
          );

          if (stage) {
            const query = {
              stage: stage._id
            };

            if (filters.multi_assigned_to && filters.multi_assigned_to.length) {
              query.assigned_to = filters.multi_assigned_to.map(
                ({ value }) => value
              );
            }

            if (filters.customer && filters.customer.length) {
              query.customer = filters.customer.map(({ value }) => value);
            }
            if (filters.project && filters.project.length) {
              query.project = filters.project.map(({ value }) => value);
            }
            if (filters.department && filters.department.length) {
              query.department = filters.department.map(({ value }) => value);
            }
            if (filters.call_type && filters.call_type.length) {
              query.call_type = filters.call_type.map(({ value }) => value);
            }

            if (filters.invoice_frequency && filters.invoice_frequency.length) {
              query.invoice_frequency = filters.invoice_frequency.map(
                ({ value }) => value
              );
            }

            if (filters.start_date) {
              query.start_date = filters.start_date;
            }
            if (filters.end_date) {
              query.end_date = filters.end_date;
            }
            if (filters.project_manager && filters.project_manager.length) {
              query.project_manager = filters.project_manager.map(
                ({ value }) => value
              );
            }
            if (filters.only_flagged) {
              query.is_flagged = true;
            }

            const result = await CallService.batchLoadCalls({
              ...query,
              groupByField: filters.grouped_by_customer
                ? "customer"
                : "assigned_to"
            });

            setGroupedCalls(result);
            actions.toggleLoader(false);
            resolve(true);
          }
        } catch (error) {
          toast.error();
          reject(error);
        } finally {
          actions.toggleLoader(false);
        }
      }),
    [actions, reviewType, codes, filters]
  );

  useEffect(() => {
    if (
      auth &&
      auth.user &&
      Array.isArray(auth.user.roles) &&
      filters &&
      Array.isArray(filters.department) &&
      filters.department.length === 0 &&
      !auth.user.roles.some((role) => role.name === "CEO") &&
      !(pathname.includes("invoice") || pathname.includes("billing"))
    ) {
      actions.toggleLoader(true);
      const department = [
        {
          value: auth.user.department._id,
          label: auth.user.department.description
        }
      ];
      setFilters({
        ...filters,
        department
      });
      actions.toggleLoader(false);
    }
    if (needFetchData && !loading && codes?.length) {
      actions.toggleLoader(true);
      setNeedFetchData(false);
      loadCalls(true);
    }

    if (!services.length) actions.loadServices();

    if (pathname !== localPathname && !loading) {
      setLocalPathname(pathname);
      setReviewType(
        (pathname.includes("finance") && "Finance") ||
          (pathname.includes("invoice") && "Invoice") ||
          (pathname.includes("billing") && "Billing") ||
          "Manager"
      );
      setNeedFetchData(true);
    }
  }, [
    state,
    auth,
    actions,
    loading,
    loadCalls,
    needFetchData,
    filters,
    services.length,
    reviewType,
    groupedCalls,
    pathname,
    localPathname,
    codes
  ]);

  const handleChange = (name) => async (value) => {
    await actions.toggleLoader(true);
    await setFilters({ ...filters, [name]: value });
    localStorage.setItem(
      "CALL_APPROVAL_FILTERS",
      JSON.stringify({ ...filters, [name]: value })
    );
    await actions.toggleLoader(false);
    setGroupedCalls({});
    setNeedFetchData(true);
  };

  const resetFilters = async () => {
    await actions.toggleLoader(true);
    setFilters(INITIAL_FILTER_STATE);
    localStorage.removeItem("CALL_APPROVAL_FILTERS");
    await actions.toggleLoader(false);
    setNeedFetchData(true);
  };

  const openFilters = () => {
    setState({ ...state, open: !state.open });
  };

  const openEmailNotification = (call) => {
    actions.setCallApprovalToNotify(call);
    actions.showModal("sendEmailModal");
  };

  const closeEmailNotificationModal = () => {
    actions.setCallApprovalToNotify({});
    actions.hideModal("sendEmailModal");
  };

  const closeInvoiceDescriptionModal = () => {
    actions.setCallApprovalToNotify({});
    actions.hideModal("sendEmailModal");
  };

  const handleExport = (data) => {
    downloadCSV(
      `InvoiceDetail-${formatDate(new Date(), "yyyyMMdd")}`,
      data,
      INVOICE_DETAIL_COLUMNS
    );
  };

  const fetchInvoiceDetailResult = async (sendingToBC = false) => {
    let stageValue;
    if (reviewType === "Invoice") {
      // Ready to Invoice
      stageValue = "80R";
    }
    const stage = codes.find(
      (c) => c.entity === "Call" && c.value === stageValue
    );

    const query = {
      stage: stage._id
    };
    if (filters.multi_assigned_to && filters.multi_assigned_to.length) {
      query.assigned_to = filters.multi_assigned_to.map(({ value }) => value);
    }
    if (filters.customer && filters.customer.length) {
      query.customer = filters.customer.map(({ value }) => value);
    }
    if (filters.project && filters.project.length) {
      query.project = filters.project.map(({ value }) => value);
    }
    if (filters.department && filters.department.length) {
      query.department = filters.department.map(({ value }) => value);
    }
    if (filters.call_type && filters.call_type.length) {
      query.call_type = filters.call_type.map(({ value }) => value);
    }
    if (filters.start_date) {
      query.start_date = filters.start_date;
    }
    if (filters.end_date) {
      query.end_date = filters.end_date;
    }
    if (filters.invoice_frequency && filters.invoice_frequency.length) {
      query.invoice_frequency = filters.invoice_frequency.map(
        ({ value }) => value
      );
    }
    if (sendingToBC) {
      query.sendingToBC = true;
    }

    const formattedQuery = queryString.stringify(query, {
      arrayFormat: "bracket"
    });
    const invoiceDetailResult = await ReportService.get(
      `/invoice-detail?${formattedQuery}`
    );

    return invoiceDetailResult;
  };

  const handleExportToCsv = async () => {
    try {
      setLoadingExport(true);
      const invoiceDetailResult = await fetchInvoiceDetailResult();

      handleExport(invoiceDetailResult);
      setLoadingExport(false);
    } catch (error) {
      toast.error();
      setLoadingExport(false);
    }
  };

  const handleExportToBC = async () => {
    try {
      setLoadingExport(true);
      const {
        invoicesToAttempt,
        invoiceLinesToAttempt,
        invoiceResult: invres
      } = await fetchInvoiceDetailResult(true);

      if (!invres?.length) {
        const invArr = [];
        invArr.push(invres);

        setInvoiceSummaryResults({
          invoices: invArr,
          totalInvoiceLines: invoiceLinesToAttempt,
          totalInvoices: invoicesToAttempt
        });
      } else {
        setInvoiceSummaryResults({
          invoices: invres,
          totalInvoiceLines: invoiceLinesToAttempt,
          totalInvoices: invoicesToAttempt
        });
      }
    } catch (error) {
      toast.error();
      setLoadingExport(false);
    } finally {
      setLoadingExport(false);
      actions.showModal("bcInvoiceExportSummaryModal");
    }
  };

  const emailForm = (
    <CallApprovalNotificationForm
      callApprovalToNotify={callApprovalToNotify}
      save={actions.notifyCallApproval}
      onClose={closeEmailNotificationModal}
      onCancel={closeEmailNotificationModal}
    />
  );

  const bcInvoiceExportSummaryModal = (
    <BcInvoiceExportSummaryModal
      errMessage={
        invoiceSummaryResults?.invoices?.status?.toString()?.slice(0, 2) ===
          "40" ||
        invoiceSummaryResults?.invoices?.status?.toString()?.slice(0, 2) ===
          "50"
          ? invoiceSummaryResults?.invoices?.message
          : ""
      }
      invoices={invoiceSummaryResults?.invoices}
      totalInvoices={invoiceSummaryResults?.totalInvoices}
      totalInvoicesLines={invoiceSummaryResults?.totalInvoiceLines}
      onClose={closeInvoiceDescriptionModal}
    />
  );

  return (
    <div className="cap-container">
      <HeaderWrapper
        auth
        breadcrumbs={[
          {
            label: `${reviewType} Call Approval`
          }
        ]}
        onClickAction={
          reviewType === "Invoice"
            ? [
                { action: openFilters, icon: <IoFunnel /> },
                {
                  action: handleExportToBC,
                  className: "btn btn-danger",
                  text: "Export To BC",
                  disabled: loading || disableInvoiceExport || loadingExport,
                  tooltipContent: disableInvoiceExport
                    ? "Please filter for Billable call type"
                    : ""
                },
                {
                  action: handleExportToCsv,
                  className: "btn btn-success",
                  text: "Export To CSV",
                  disabled: loading || disableInvoiceExport || loadingExport,
                  tooltipContent: disableInvoiceExport
                    ? "Please filter for Billable call type"
                    : ""
                }
              ]
            : [{ action: openFilters, icon: <IoFunnel /> }]
        }
      />
      {state.open && (
        <React.Fragment>
          <Formsy
            autoComplete="off"
            onInvalid={() => null}
            onValid={() => null}
            onValidSubmit={() => {
              setNeedFetchData(true);
            }}
          >
            <CallApprovalFilters
              displayGroupByCustomer
              filters={filters}
              setCustomers={setCustomers}
              setProjects={setProjects}
              onChange={handleChange}
              setLoading={actions.toggleLoader}
              loading={loading}
              displayInvoiceFrequencyFilter={reviewType === "Invoice"}
            >
              <button
                className="btn btn-warning"
                type="submit"
                disabled={loading}
                onClick={resetFilters}
                style={{ maxHeight: 38 }}
              >
                Reset
              </button>
            </CallApprovalFilters>
          </Formsy>
        </React.Fragment>
      )}
      {groupedCalls && !loading && (
        <div className="call-page-header-titles">
          <span>{filters.grouped_by_customer ? "Customer" : "User"}</span>
          <span>Calls</span>
          <span />
          <div className="cp-footer-totals">
            <span>Actual Hours</span>
            <span>Billable Hours</span>
            <span>Non Billable Hours</span>
          </div>
        </div>
      )}
      <div
        className="content-wrapper cap-wrapper wrapper-scroll-12"
        style={{
          height: state.open ? "calc(100vh - 260px)" : "calc(100vh - 95px)"
        }}
      >
        {loading && (
          <div className="loading-loader-load-container">
            <Loading active={loading} />
          </div>
        )}
        {groupedCalls &&
          !loading &&
          Object.keys(groupedCalls).map((userId) => (
            <div className="row-wrapper" key={userId}>
              <CallApprovalContainer
                {...{
                  customers,
                  projects,
                  services,
                  codes,
                  auth,
                  users,
                  reviewType,
                  calls: groupedCalls[userId],
                  loadCalls,
                  userId,
                  onClearCalls: actions.clearApprovedCalls,
                  setNeedFetchData,
                  openEmailNotification,
                  filters
                }}
              />
            </div>
          ))}
      </div>
      <Modal
        id="sendEmailModal"
        title={`Send an alert to ${
          callApprovalToNotify?.user?.full_name || ""
        } regarding to this call`}
        body={emailForm}
        close={actions.hideModal}
      />
      <Modal
        id="bcInvoiceExportSummaryModal"
        title="Business Central Invoice Export Summary"
        body={bcInvoiceExportSummaryModal}
        close={actions.hideModal}
      />
      <ConfirmModal
        id="callSaveModal"
        title="Save Call"
        body="Do you want to save changes to this call?"
        close={actions.hideModal}
        confirm={() => true}
      />
    </div>
  );
};

CallApprovalPage.propTypes = {
  auth: PropTypes.object.isRequired,
  actions: PropTypes.object,
  codes: PropTypes.array,
  users: PropTypes.array,
  services: PropTypes.array,
  pathname: PropTypes.string,
  loading: PropTypes.bool,
  callApprovalToNotify: PropTypes.object
};

const mapStatesToProps = (state) => ({
  state: state.reducers,
  auth: state.reducers.auth,
  callsToApprove: state.reducers.callsToApprove,
  services: state.reducers.services,
  users: state.reducers.users,
  codes: state.reducers.codes,
  loading: state.reducers.loading || false,
  callApprovalToNotify: state.reducers.callApprovalToNotify
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      ...modalActions,
      ...callActions,
      ...serviceActions,
      ...userActions,
      ...codeActions,
      ...loaderActions
    },
    dispatch
  )
});

export default connect(mapStatesToProps, mapDispatchToProps)(CallApprovalPage);
